import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Plus, Edit2, Trash2, X, MapPin, Briefcase, 
  Clock, LogOut, LayoutDashboard, Search,
  AlertCircle, CheckCircle2
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getJobs, saveJob, updateJob, deleteJob } from "../lib/jobStore";
import { logout, onAuthChange } from "../lib/adminAuth";

export default function AdminDashboard() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({ role: "", location: "", type: "Full-time", description: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  const navigate = useNavigate();
  const username = localStorage.getItem("atf_admin_user");

  // Guard: redirect to login if Firebase says user is not authenticated
  useEffect(() => {
    const unsubscribe = onAuthChange((user) => {
      if (!user) {
        navigate("/admin/login");
      } else {
        fetchJobs();
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const fetchJobs = async () => {
    try {
      const data = await getJobs();
      setJobs(data);
    } catch (err) {
      setError("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    try {
      if (editingJob) {
        await updateJob(editingJob.id, formData);
        setSuccess("Job updated successfully");
      } else {
        await saveJob(formData);
        setSuccess("Job created successfully");
      }
      setIsModalOpen(false);
      setEditingJob(null);
      setFormData({ role: "", location: "", type: "Full-time", description: "" });
      fetchJobs();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this job?")) return;
    try {
      await deleteJob(id);
      setSuccess("Job deleted successfully");
      fetchJobs();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const openModal = (job = null) => {
    if (job) {
      setEditingJob(job);
      setFormData({ role: job.role, location: job.location, type: job.type, description: job.description || "" });
    } else {
      setEditingJob(null);
      setFormData({ role: "", location: "", type: "Full-time", description: "" });
    }
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#F9F8F5] flex">
      {/* Sidebar */}
      <aside className="w-80 bg-[#141414] text-white p-8 flex flex-col fixed h-full z-20">
        <div className="mb-12">
          <span className="font-sans text-[10px] font-bold tracking-[0.3em] text-[#835F21] uppercase block mb-1">Academy Portal</span>
          <h2 className="font-display font-medium text-2xl tracking-tight">ATF Careers</h2>
        </div>

        <nav className="flex-1 space-y-2">
          <div className="flex items-center gap-4 p-4 bg-[#835F21]/10 border border-[#835F21]/20 rounded-2xl text-[#835F21]">
            <LayoutDashboard size={20} />
            <span className="font-sans text-[13px] font-bold uppercase tracking-wider">Job Management</span>
          </div>
        </nav>

        <div className="mt-auto border-t border-white/10 pt-8">
          <div className="flex items-center gap-4 mb-8 px-4">
            <div className="w-10 h-10 rounded-full bg-[#835F21] flex items-center justify-center font-bold font-sans text-sm">
              {username?.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-sans text-[13px] font-bold uppercase tracking-wide">{username}</p>
              <p className="font-sans text-[10px] text-white/40 uppercase tracking-widest font-bold">Admin Level</p>
            </div>
          </div>
          <button 
            onClick={handleLogout}
            className="flex items-center gap-4 w-full p-4 hover:bg-white/5 rounded-2xl text-white/60 hover:text-white transition-all group"
          >
            <LogOut size={20} className="group-hover:translate-x-1 transition-transform" />
            <span className="font-sans text-[11px] font-bold uppercase tracking-widest">Terminate Session</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-80 p-12">
        <div className="max-w-6xl mx-auto">
          <header className="flex items-center justify-between mb-16">
            <div>
              <h1 className="font-display font-medium text-4xl text-[#141414] tracking-tight mb-2">Active Deployments</h1>
              <p className="font-sans text-[#141414]/50 text-sm">Manage professional security listings across Pan-India sectors.</p>
            </div>
            <button 
              onClick={() => openModal()}
              className="bg-[#141414] hover:bg-[#835F21] text-white h-14 px-8 rounded-2xl flex items-center gap-3 transition-all duration-300 shadow-xl shadow-black/5"
            >
              <Plus size={20} />
              <span className="font-sans text-[11px] font-bold uppercase tracking-[0.15em]">Create New Listing</span>
            </button>
          </header>

          <AnimatePresence>
            {success && (
              <motion.div 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="bg-green-50 border border-green-100 p-4 rounded-2xl flex items-center gap-3 text-green-700 mb-8"
              >
                <CheckCircle2 size={18} />
                <span className="font-sans text-[13px] font-medium uppercase tracking-wide">{success}</span>
              </motion.div>
            )}
            {error && (
              <motion.div 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="bg-red-50 border border-red-100 p-4 rounded-2xl flex items-center gap-3 text-red-700 mb-8"
              >
                <AlertCircle size={18} />
                <span className="font-sans text-[13px] font-medium uppercase tracking-wide">{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="grid grid-cols-1 gap-4">
            {loading ? (
              <div className="py-20 text-center font-sans text-[#141414]/20 uppercase tracking-[0.3em] font-bold">Initalizing Database...</div>
            ) : jobs.length === 0 ? (
              <div className="py-20 text-center bg-white border border-dashed border-[#141414]/10 rounded-3xl">
                <Briefcase size={40} className="mx-auto text-[#141414]/10 mb-4" />
                <p className="font-sans text-[#141414]/40 text-sm uppercase font-bold tracking-widest">No active deployments found</p>
              </div>
            ) : (
              jobs.map((job) => (
                <motion.div 
                  key={job.id}
                  layout
                  className="bg-white border border-[#141414]/5 rounded-3xl p-8 flex items-center justify-between hover:shadow-[0_20px_60px_rgba(0,0,0,0.03)] transition-all group"
                >
                  <div className="flex items-center gap-8">
                    <div className="w-16 h-16 rounded-2xl bg-[#F9F8F5] flex items-center justify-center text-[#835F21] group-hover:bg-[#835F21] group-hover:text-white transition-all duration-500">
                      <Briefcase size={24} />
                    </div>
                    <div>
                      <h3 className="font-display font-medium text-xl text-[#141414] mb-2">{job.role}</h3>
                      <div className="flex items-center gap-6">
                        <span className="flex items-center gap-2 font-sans text-[11px] font-bold uppercase tracking-wider text-[#141414]/40">
                          <MapPin size={14} className="text-[#835F21]" /> {job.location}
                        </span>
                        <span className="flex items-center gap-2 font-sans text-[11px] font-bold uppercase tracking-wider text-[#141414]/40">
                          <Clock size={14} className="text-[#835F21]" /> {job.type}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      onClick={() => openModal(job)}
                      className="w-12 h-12 rounded-xl bg-[#F9F8F5] flex items-center justify-center text-[#141414]/60 hover:bg-[#141414] hover:text-white transition-all"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button 
                      onClick={() => handleDelete(job.id)}
                      className="w-12 h-12 rounded-xl bg-[#F9F8F5] flex items-center justify-center text-[#141414]/60 hover:bg-red-600 hover:text-white transition-all"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </main>

      {/* Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="absolute inset-0 bg-[#141414]/80 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-xl bg-white rounded-[2rem] shadow-2xl flex flex-col max-h-[90vh]"
            >
              {/* Sticky header */}
              <div className="flex items-center justify-between px-10 pt-10 pb-6 border-b border-[#141414]/5 flex-shrink-0">
                <div>
                  <h2 className="font-display font-medium text-2xl text-[#141414] tracking-tight">
                    {editingJob ? "Refine Deployment" : "New Deployment"}
                  </h2>
                  <p className="font-sans text-[11px] text-[#141414]/30 uppercase tracking-widest font-bold mt-1">
                    {editingJob ? "Update listing details" : "Create a new job listing"}
                  </p>
                </div>
                <button onClick={() => setIsModalOpen(false)} className="w-10 h-10 rounded-xl bg-[#F9F8F5] flex items-center justify-center text-[#141414]/40 hover:text-[#141414] hover:bg-[#141414]/5 transition-all">
                  <X size={20} />
                </button>
              </div>

              {/* Scrollable form body */}
              <div className="overflow-y-auto flex-1 px-10 py-8">
                <form onSubmit={handleSubmit} className="space-y-6" id="job-form">
                  <div className="space-y-2">
                    <label className="font-sans text-[10px] font-bold tracking-[0.2em] uppercase text-[#141414]/40 ml-1">Job Role / Title</label>
                    <input 
                      type="text"
                      required
                      value={formData.role}
                      onChange={(e) => setFormData({...formData, role: e.target.value})}
                      className="w-full h-14 bg-[#F9F8F5] border-none rounded-2xl px-6 font-sans text-sm outline-none focus:ring-2 focus:ring-[#835F21]/20 transition-all"
                      placeholder="e.g. Senior Security Supervisor"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="font-sans text-[10px] font-bold tracking-[0.2em] uppercase text-[#141414]/40 ml-1">Regional Sector / Location</label>
                    <input 
                      type="text"
                      required
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      className="w-full h-14 bg-[#F9F8F5] border-none rounded-2xl px-6 font-sans text-sm outline-none focus:ring-2 focus:ring-[#835F21]/20 transition-all"
                      placeholder="e.g. Western Zone (Mumbai)"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="font-sans text-[10px] font-bold tracking-[0.2em] uppercase text-[#141414]/40 ml-1">Engagement Type</label>
                    <select 
                      value={formData.type}
                      onChange={(e) => setFormData({...formData, type: e.target.value})}
                      className="w-full h-14 bg-[#F9F8F5] border-none rounded-2xl px-6 font-sans text-sm outline-none focus:ring-2 focus:ring-[#835F21]/20 transition-all appearance-none cursor-pointer"
                    >
                      <option value="Full-time">Full-time Engagement</option>
                      <option value="Contract">Project Based Contract</option>
                      <option value="Part-time">Support / Part-time</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="font-sans text-[10px] font-bold tracking-[0.2em] uppercase text-[#141414]/40 ml-1">Job Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full bg-[#F9F8F5] border-none rounded-2xl px-6 py-4 font-sans text-sm outline-none focus:ring-2 focus:ring-[#835F21]/20 transition-all resize-none"
                      rows={5}
                      placeholder="Describe the role, responsibilities, requirements, and any other relevant details for candidates..."
                    />
                  </div>
                </form>
              </div>

              {/* Sticky footer with submit button */}
              <div className="px-10 py-6 border-t border-[#141414]/5 flex-shrink-0 bg-white rounded-b-[2rem]">
                <button 
                  type="submit"
                  form="job-form"
                  className="w-full h-14 bg-[#141414] hover:bg-[#835F21] text-white rounded-2xl font-sans text-[11px] font-bold tracking-[0.2em] uppercase transition-all duration-300 shadow-xl shadow-black/5"
                >
                  {editingJob ? "Confirm Modifications" : "Authenticate & Publish"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
